import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useApp } from "@/lib/AppContext";

const T = {
  ar: {
    title: "قيّم تجربتك",
    subtitle: "رأيك يساعدنا على تقديم خدمة أفضل",
    namePlaceholder: "اسمك",
    commentPlaceholder: "شاركنا تجربتك...",
    submit: "إرسال التقييم",
    submitting: "⏳ جاري الإرسال...",
    successTitle: "شكراً لتقييمك!",
    successMsg: "تم استلام تقييمك وسيتم مراجعته",
    ratingLabels: ["ضعيف", "مقبول", "جيد", "جيد جداً", "ممتاز"],
    nameRequired: "يرجى إدخال اسمك",
    ratingRequired: "يرجى اختيار تقييم",
  },
  en: {
    title: "Rate Your Experience",
    subtitle: "Your feedback helps us serve you better",
    namePlaceholder: "Your name",
    commentPlaceholder: "Share your experience...",
    submit: "Submit Review",
    submitting: "⏳ Submitting...",
    successTitle: "Thank you!",
    successMsg: "Your review has been received and will be reviewed",
    ratingLabels: ["Poor", "Fair", "Good", "Very Good", "Excellent"],
    nameRequired: "Please enter your name",
    ratingRequired: "Please select a rating",
  }
};

export default function RatingPage() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";

  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("orderId");
  const orderNumber = params.get("orderNumber");
  const customerName = params.get("name") || "";

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [name, setName] = useState(customerName);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError(tr.nameRequired); return; }
    if (!rating) { setError(tr.ratingRequired); return; }
    setError("");
    setLoading(true);
    await base44.entities.Review.create({
      customer_name: name.trim(),
      rating,
      comment: comment.trim(),
      order_id: orderId || "",
      order_number: orderNumber || "",
      is_approved: false,
      is_featured: false,
    });
    setDone(true);
    setLoading(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={isAr ? "rtl" : "ltr"}>
        <div className="bg-card rounded-3xl p-10 text-center max-w-sm w-full shadow-xl border border-border">
          <div className="text-6xl mb-4">⭐</div>
          <h2 className="text-2xl font-black text-foreground mb-2">{tr.successTitle}</h2>
          <p className="text-muted-foreground">{tr.successMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={isAr ? "rtl" : "ltr"}>
      <div className="bg-card rounded-3xl p-8 max-w-sm w-full shadow-xl border border-border">
        <div className="text-center mb-6">
          <img
            src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png"
            alt="logo"
            className="w-16 h-16 object-contain mx-auto mb-3"
          />
          <h2 className="text-xl font-black text-foreground">{tr.title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{tr.subtitle}</p>
          {orderNumber && (
            <p className="text-xs text-primary font-bold mt-1">#{orderNumber}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Stars */}
          <div className="flex justify-center gap-2 py-2">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(0)}
                className="text-4xl transition-transform hover:scale-110"
              >
                {star <= (hovered || rating) ? "⭐" : "☆"}
              </button>
            ))}
          </div>
          {(hovered || rating) > 0 && (
            <p className="text-center text-sm font-bold text-primary">
              {tr.ratingLabels[(hovered || rating) - 1]}
            </p>
          )}

          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={tr.namePlaceholder}
            className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary text-foreground"
          />

          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={tr.commentPlaceholder}
            rows={3}
            className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary text-foreground resize-none"
          />

          {error && (
            <p className="text-xs text-red-500 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground font-black py-3.5 rounded-2xl hover:opacity-90 transition-all disabled:opacity-60"
          >
            {loading ? tr.submitting : tr.submit}
          </button>
        </form>
      </div>
    </div>
  );
}